import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Settings, LogOut,
  ChevronRight, Clock, FileText, Bookmark, MapPinned, Mail, CloudSun,
} from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { AppBrandFooter } from '../common/AppBrandFooter';
import { ContributorInviteSection } from './ContributorInviteSection';
import { InstallAppSection } from '../pwa/InstallAppSection';

// Generate initials from name (first letter of first + last name)
function getInitials(name?: string): string {
  if (!name) return '?';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

interface ProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings?: () => void;
  /** Main map: open assignments picker (no-op when disabled). */
  onBrowseFieldCampaigns?: () => void;
  fieldCampaignsDisabled?: boolean;
  fieldCampaignsSubtitle?: string;
  onOpenContributions?: () => void;
  onOpenSavedPlaces?: () => void;
  onOpenFindPostcode?: () => void;
  onOpenRecentActivity?: () => void;
  onOpenWeatherForecast?: () => void;
}

export function ProfilePanel({
  isOpen,
  onClose,
  onOpenSettings,
  onBrowseFieldCampaigns,
  fieldCampaignsDisabled = false,
  fieldCampaignsSubtitle,
  onOpenContributions,
  onOpenSavedPlaces,
  onOpenFindPostcode,
  onOpenRecentActivity,
  onOpenWeatherForecast,
}: ProfilePanelProps) {
  const { user, logout, isAuthenticated } = useAuthStore();

  const handleLogout = () => {
    logout();
    onClose();
  };

  if (!isOpen || !isAuthenticated) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 30, stiffness: 300 }}
        className="fixed inset-0 bg-white dark:bg-gray-900 z-[90] flex flex-col"
      >
        <div className="flex-1 overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 pb-4 flex items-center justify-between pt-safe z-10">
            <h2 className="text-lg font-semibold dark:text-white">
              {user?.name ? `Hi, ${user.name.split(' ')[0]}!` : 'Profile'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 -mr-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center">
                {user?.avatar ? (
                  <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {getInitials(user?.name)}
                  </span>
                )}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">{user?.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
                {user?.phone && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.phone}</p>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="p-4 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={() => onOpenSavedPlaces?.()}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Bookmark size={20} className="text-purple-500 dark:text-purple-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">Saved Places</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Home, work, and bookmarked locations
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            <button
              onClick={() => onOpenFindPostcode?.()}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Mail size={20} className="text-emerald-500 dark:text-emerald-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">Find My Postcode</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Drop a pin or use your location
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            <button
              type="button"
              onClick={() => onOpenWeatherForecast?.()}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <CloudSun size={20} className="text-sky-500 dark:text-sky-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">Weather Forecast</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  7-day map overlay and dashboard
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            {onBrowseFieldCampaigns && !fieldCampaignsDisabled && (
              <button
                type="button"
                onClick={onBrowseFieldCampaigns}
                className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <MapPinned size={20} className="text-cyan-600 dark:text-cyan-400 shrink-0" />
                <div className="flex-1 text-left">
                  <span className="block text-gray-700 dark:text-gray-200">Field campaigns</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {fieldCampaignsSubtitle ||
                      'Open your assigned campaign list on the map'}
                  </span>
                </div>
                <ChevronRight size={20} className="text-gray-400 shrink-0" />
              </button>
            )}

            <button
              onClick={() => onOpenRecentActivity?.()}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Clock size={20} className="text-gray-500 dark:text-gray-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">Recent Activity</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Your recent searches on the map
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            <button
              onClick={() => onOpenContributions?.()}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <FileText size={20} className="text-primary-500 dark:text-primary-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">My Contributions</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Mapping submissions and earnings
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            <button
              onClick={() => {
                onClose();
                onOpenSettings?.();
              }}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Settings size={20} className="text-gray-500 dark:text-gray-400 shrink-0" />
              <div className="flex-1 text-left">
                <span className="block text-gray-700 dark:text-gray-200">Settings</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Appearance, privacy, and account options
                </span>
              </div>
              <ChevronRight size={20} className="text-gray-400 shrink-0" />
            </button>

            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-red-600 dark:text-red-400"
            >
              <LogOut size={20} />
              <span className="flex-1 text-left">Sign Out</span>
            </button>

            <InstallAppSection variant="menu" className="border-t border-gray-100 dark:border-gray-800 mt-1 pt-1" />
          </div>

          <ContributorInviteSection />

          <AppBrandFooter className="p-4 border-t border-gray-100 dark:border-gray-800" />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
